import settings from '@/constants/constants';
import { FF_ENABLE_IMMEDIATE_CONSULTS_PROMO_CODES } from '@/constants/featureFlags';
import { IMMEDIATE_NURSE_CONSULT_TRACKING_ID } from '@/constants/immediateNurseConsultation';
import { GoogleAnalyticsEventName } from '@/constants/types';
import { useConsultEligibleCalendar } from '@/context/calendar-context';
import type { PreScreeningData } from '@/context/pre-screening-context';
import { usePrescreeningData } from '@/context/pre-screening-context';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import useGoogleTagManager from '@/hooks/useGoogleTagManager';
import {
  ImmediateNurseConsultationVariant,
  useImmediateNurseConsultVariant,
} from '@/hooks/useImmediateNurseConsultVariant';
import type { PatchPatientForInstantConsultationReply } from '@/hooks/useInstantConsultRequest';
import { useInstantConsultRequest } from '@/hooks/useInstantConsultRequest';
import ResendSmsVerificationDialog from '@/pages/components/sms-verification/ResendSmsVerificationDialog';
import { CtaButton, CtaButtonText, CtaSection } from '@/pages/components/styled/Cta';
import { formatStringToSha256Hash } from '@/utils/ga-hashing';
import { getRateLimitIndexByTimeout } from '@/utils/immediate-nurse-consult';
import { getSearchParams } from '@/utils/url';
import { Alert, Box, Button, Link, Stack, Typography } from '@montugroup/design-system';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PhoneEnabledOutlinedIcon from '@mui/icons-material/PhoneEnabledOutlined';
import PhoneIphoneOutlinedIcon from '@mui/icons-material/PhoneIphoneOutlined';
import QuizOutlinedIcon from '@mui/icons-material/QuizOutlined';
import { styled } from '@mui/material';
import { lightBlue } from '@mui/material/colors';
import { useState } from 'react';

export const ButtonWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  position: 'fixed',
  bottom: 0,
  width: '100%',
  justifyContent: 'flex-end',
  padding: '1rem 1.5rem',
  zIndex: 1,
  left: 0,
  flexDirection: 'column',
  gap: '0.5rem',

  [theme.breakpoints.up('md')]: {
    position: 'inherit',
    padding: '1.5rem 0',
    gap: '1rem',
    justifyContent: 'initial',
    flexDirection: 'row',

    ['button:first-of-type']: {
      marginRight: 0,
    },
  },
}));

const StyledStack = styled(Stack)(({ theme }) => ({
  flexDirection: 'column',
  gap: theme.spacing(4),
  marginBottom: theme.spacing(60),
  [theme.breakpoints.up('sm')]: {
    marginBottom: 0,
  },
}));

const NurseConsultationHeading = styled(Typography)(({ theme }) => ({
  fontSize: theme.spacing(5),
  fontWeight: 'bold',
  color: theme.palette.primary.main,
}));

const NurseConsultationSubHeading = styled(Typography)(({ theme }) => ({
  fontSize: theme.spacing(4),
  fontWeight: 'bold',
}));

const StyledAlert = styled(Alert)(({ theme }) => ({
  width: '100%',
  marginBottom: theme.spacing(4),
  [theme.breakpoints.up('md')]: {
    marginBottom: 0,
  },
}));

const DefaultNurseConsultationInfo = () => (
  <Box
    sx={{
      marginBottom: 72,
    }}
  >
    <PhoneEnabledOutlinedIcon color="secondary" sx={{ fontSize: '2rem', marginBottom: 4 }} />
    <Typography variant="h6" component="h6" fontWeight={'bold'} marginBottom={4}>
      Your initial assessment
    </Typography>
    <Typography marginBottom={4}>
      During this consultation you and a nurse will discuss your medical history and our services.
    </Typography>
    <Typography marginBottom={8}>Feel free to ask any questions you have.</Typography>
    <Typography marginBottom={4} fontWeight={'bold'}>
      What’s next?
    </Typography>
    <Typography marginBottom={4}>
      If a nurse deems you eligible for the service, they’ll organise your booking with a doctor, and you’ll receive an
      email with next steps.
    </Typography>
  </Box>
);

const DefaultNurseConsultationButtons = ({
  showScheduleForLaterButton,
  handleAnalyticsOnClick,
  handleShowCalendar,
}: {
  showScheduleForLaterButton: boolean;
  handleAnalyticsOnClick: () => void;
  handleShowCalendar: () => void;
}) => {
  return (
    <>
      <Button
        variant="outlined"
        startIcon={<PhoneIphoneOutlinedIcon />}
        onClick={() => {
          handleAnalyticsOnClick();
          window.location.href = `tel:${settings.instantConnectPhoneNumber}`;
        }}
      >
        Disconnected? Call nurse again
      </Button>
      {showScheduleForLaterButton && (
        <>
          <Typography sx={{ textAlign: 'center' }}>or</Typography>
          <Button
            variant="contained"
            startIcon={<CalendarTodayIcon />}
            color="primary"
            fullWidth
            onClick={handleShowCalendar}
          >
            Schedule for later
          </Button>
        </>
      )}
    </>
  );
};

const DefaultAssistanceInfo = () => (
  <Alert
    severity="info"
    icon={<QuizOutlinedIcon />}
    sx={{
      backgroundColor: lightBlue[50],
      marginTop: 10,
    }}
  >
    Need assistance? Visit our{' '}
    <Link
      href="https://support.alternaleaf.com.au/hc/en-au"
      rel="noopener noreferrer"
      target="_blank"
      sx={{
        fontWeight: 'bold',
      }}
    >
      help centre
    </Link>{' '}
    for answers to frequently asked questions or to find{' '}
    <Link
      href="https://support.alternaleaf.com.au/hc/en-au/articles/34017993658905-Contact-Alternaleaf"
      rel="noopener noreferrer"
      target="_blank"
      sx={{
        fontWeight: 'bold',
      }}
    >
      our contact details
    </Link>{' '}
    for further support.
  </Alert>
);

const AmazonConnectInitiatedNurseConsultationInfo = ({
  handleResendVerification,
}: {
  handleResendVerification: () => void;
}) => (
  <StyledStack>
    <PhoneEnabledOutlinedIcon color="secondary" sx={{ fontSize: '2rem' }} />
    <NurseConsultationHeading>Your initial assessment</NurseConsultationHeading>
    <Typography>
      Once you have verified your phone number, you will receive a call from Alternaleaf and be connected to one of our
      nurses for your consultation.{' '}
      <Link onClick={handleResendVerification} data-testid={'resend-sms-link'} sx={{ cursor: 'pointer' }}>
        Didn’t receive an SMS?
      </Link>
    </Typography>
    <Typography>
      During this consultation you and a nurse will discuss your medical history and our services. Feel free to ask any
      questions you have.
    </Typography>
    <NurseConsultationSubHeading>What’s next?</NurseConsultationSubHeading>
    <Typography>
      If a nurse deems you eligible for the service, they’ll organise your booking with a doctor, and you’ll receive an
      email with next steps.
    </Typography>
    <NurseConsultationSubHeading>Disconnected?</NurseConsultationSubHeading>
    <Typography>
      Our nurse will attempt to re-call you to resume your consultation. In the event the consultation does not resume,
      you can call our support team or schedule your consult for later.
    </Typography>
  </StyledStack>
);

export const NurseConsultation = () => {
  const [resendSmsVerificationDialogOpen, setResendSmsVerificationDialogOpen] = useState(false);
  const { preScreeningData, setPrescreeningData } = usePrescreeningData();
  const { handleShowCalendar } = useConsultEligibleCalendar();
  const { flags } = useFeatureFlags();
  const isPromoCodeEnabled = flags[FF_ENABLE_IMMEDIATE_CONSULTS_PROMO_CODES];

  const userEmail = getSearchParams('email', { localStorageKey: 'prescreeningUserEmail' });
  const shaEmail = userEmail ? formatStringToSha256Hash(userEmail) : '';
  const promoCode = isPromoCodeEnabled ? getSearchParams('promocode') : null;
  const { sendInstantConsultRequest } = useInstantConsultRequest();

  const onSubmit = async (): Promise<PatchPatientForInstantConsultationReply> => {
    try {
      const responseBody = await sendInstantConsultRequest({
        variant: 'variant-amazon-connect-initiated-inc',
        attributes: {
          firstName: preScreeningData?.patientDetails.firstName ?? '',
          lastName: preScreeningData?.patientDetails.lastName ?? '',
          phone: preScreeningData?.patientDetails.phone ?? '',
          email: userEmail ?? '',
          ...(promoCode ? { promoCode } : {}),
          ...(preScreeningData?.patientDetails.callType ? { callType: preScreeningData.patientDetails.callType } : {}),
          intakeVariant: 'INC',
        },
      });

      const rateLimitIndex = getRateLimitIndexByTimeout(responseBody?.data?.retryAfterMs || 0);
      setPrescreeningData((current: PreScreeningData) => {
        return {
          ...current,
          smsVerification: {
            rateLimitIndex,
          },
        };
      });

      const storedTrackingPayload = localStorage.getItem(IMMEDIATE_NURSE_CONSULT_TRACKING_ID);
      if (storedTrackingPayload) {
        const payload = JSON.parse(storedTrackingPayload);
        sendGoogleAnalyticsEvent(GoogleAnalyticsEventName.SUBMIT_BOOKING, payload);
      }
      return responseBody;
    } catch (error) {
      console.error(
        'Failed to send SMS verification request:',
        error instanceof Error ? error.message : JSON.stringify(error),
      );

      return {
        data: {
          retryAfterMs: 0,
        },
      };
    }
  };

  const { sendGoogleAnalyticsEvent } = useGoogleTagManager();

  const immediateConsultVariantFromLd = useImmediateNurseConsultVariant();
  const immediateConsultVariant = preScreeningData?.immediateNurseConsultation.variant ?? immediateConsultVariantFromLd;

  const handleAnalyticsOnClick = () => {
    const storedTrackingPayload = localStorage.getItem(IMMEDIATE_NURSE_CONSULT_TRACKING_ID);
    if (storedTrackingPayload) {
      sendGoogleAnalyticsEvent(GoogleAnalyticsEventName.SUBMIT_BOOKING, JSON.parse(storedTrackingPayload));
    }
  };

  const handleAnalyticsOnScheduleForLaterClick = () => {
    const trackingPayload = {
      event_category: 'cal.com',
      event_label: 'Initial Consultation',
      sha_email: shaEmail,
      ...(promoCode ? { promoCode } : {}),
    };
    sendGoogleAnalyticsEvent(GoogleAnalyticsEventName.START_BOOKING, trackingPayload);
  };

  const handleScheduleForLaterOnClick = () => {
    handleAnalyticsOnScheduleForLaterClick();
    handleShowCalendar();
  };

  return immediateConsultVariant === ImmediateNurseConsultationVariant.VARIANT_AMAZON_CONNECT_INITIATED_INC ? (
    <>
      <AmazonConnectInitiatedNurseConsultationInfo
        handleResendVerification={() => setResendSmsVerificationDialogOpen(true)}
      />
      <CtaSection sx={{ flexDirection: 'column' }}>
        <StyledAlert severity="info" icon={<QuizOutlinedIcon />}>
          Need assistance? Visit our{' '}
          <Link href="https://support.alternaleaf.com.au/hc/en-au" rel="noopener noreferrer" target="_blank">
            help centre
          </Link>{' '}
          for answers to frequently asked questions or for further support.
        </StyledAlert>
        <CtaButton
          variant="contained"
          startIcon={<CalendarTodayIcon />}
          color="primary"
          onClick={handleScheduleForLaterOnClick}
          size="large"
        >
          <CtaButtonText fontWeight="bold">Schedule for later</CtaButtonText>
        </CtaButton>
      </CtaSection>
      <ResendSmsVerificationDialog
        open={resendSmsVerificationDialogOpen}
        onClose={() => setResendSmsVerificationDialogOpen(false)}
        onSubmit={onSubmit}
        handleShowCalendar={handleScheduleForLaterOnClick}
      />
    </>
  ) : (
    <>
      <DefaultNurseConsultationInfo />
      <ButtonWrapper style={{ backgroundColor: 'white' }}>
        <DefaultNurseConsultationButtons
          showScheduleForLaterButton={true}
          handleAnalyticsOnClick={handleAnalyticsOnClick}
          handleShowCalendar={handleShowCalendar}
        />
        <DefaultAssistanceInfo />
      </ButtonWrapper>
    </>
  );
};
